import { useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Heart, GitCompareArrows, X, Check } from "lucide-react";
import PropertyCard from "../PropertyCard";
import SavedPropertyEliteTeaser from "./SavedPropertyEliteTeaser";

const SavedTab = ({
  savedProperties,
  savedPropertyIds,
  handleSaveToggle,
  onPropertyClick,
  onCompare,
  subscription,
}) => {
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const toggleSelect = (savedId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(savedId)) {
        next.delete(savedId);
      } else if (next.size < 4) {
        next.add(savedId);
      }
      return next;
    });
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setSelected(new Set());
  };

  const handleCompare = () => {
    const items = savedProperties.filter((s) => selected.has(s.saved_id));
    if (items.length >= 2 && onCompare) {
      onCompare(items);
    }
    exitCompareMode();
  };

  return (
    <>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">
            Saved Properties
          </h2>
          <p className="text-muted-foreground text-sm">
            Properties you've bookmarked for further analysis
          </p>
        </div>
        {savedProperties.length >= 2 && (
          <div className="flex items-center gap-2">
            {compareMode ? (
              <>
                <Badge variant="outline" className="text-xs" data-testid="compare-count-badge">
                  {selected.size}/4 selected
                </Badge>
                <Button
                  size="sm"
                  onClick={handleCompare}
                  disabled={selected.size < 2}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="compare-properties-btn"
                >
                  <GitCompareArrows className="w-4 h-4 mr-1.5" />
                  Compare ({selected.size})
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={exitCompareMode}
                  data-testid="compare-cancel-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCompareMode(true)}
                data-testid="enter-compare-mode-btn"
              >
                <GitCompareArrows className="w-4 h-4 mr-1.5" />
                Compare
              </Button>
            )}
          </div>
        )}
      </div>

      {compareMode && (
        <div className="mb-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-2.5 text-sm text-emerald-500" data-testid="compare-mode-hint">
          Select 2-4 properties to compare side-by-side. Click on a property card to select it.
        </div>
      )}

      {savedProperties.length === 0 ? (
        <Card className="bg-card border-border p-12 text-center" data-testid="saved-empty-state">
          <Heart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Saved Properties</h3>
          <p className="text-muted-foreground">
            Click the heart icon on any property to save it
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {savedProperties.map((saved) => (
            <div key={saved.saved_id} className="relative">
              {compareMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelect(saved.saved_id); }}
                  className={`absolute top-3 right-3 z-20 w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${
                    selected.has(saved.saved_id)
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : "bg-card/80 border-muted-foreground/40 text-muted-foreground hover:border-emerald-500"
                  }`}
                  data-testid={`compare-checkbox-${saved.saved_id}`}
                >
                  {selected.has(saved.saved_id) && <Check className="w-4 h-4" />}
                </button>
              )}
              <div
                className={`transition-all ${
                  compareMode && selected.has(saved.saved_id)
                    ? "ring-2 ring-emerald-500 rounded-xl"
                    : ""
                }`}
                onClick={() => {
                  if (compareMode) {
                    toggleSelect(saved.saved_id);
                  }
                }}
              >
                <PropertyCard
                  property={saved.property}
                  analysis={saved.analysis}
                  onClick={compareMode ? undefined : () => onPropertyClick(saved)}
                  isSaved={true}
                  onSaveToggle={handleSaveToggle}
                />
                {!compareMode && (
                  <SavedPropertyEliteTeaser
                    subscription={subscription}
                    property={saved.property}
                    analysis={saved.analysis}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SavedTab;
